<template>
  <div class="gym-page docs-page" id="top">

    <GymNavBar />

    <div class="docs-layout">

      <!-- ══════════════════════════════════════════
           MOBILE TOP BAR
      ══════════════════════════════════════════ -->
      <div class="docs-mobile-bar">
        <button class="docs-mobile-menu-btn" @click="sidebarOpen = true" aria-label="Άνοιγμα μενού">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
        </button>
        <span class="docs-mobile-title">{{ activeItem ? activeItem.label : 'Τεκμηρίωση' }}</span>
      </div>

      <!-- Backdrop -->
      <div class="docs-backdrop" :class="{ open: sidebarOpen }" @click="sidebarOpen = false"></div>

      <!-- ══════════════════════════════════════════
           SIDEBAR
      ══════════════════════════════════════════ -->
      <aside class="docs-sidebar" :class="{ open: sidebarOpen }">
        <div class="docs-sidebar-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="opacity:0.6">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
          </svg>
          <span>Τεκμηρίωση</span>
          <button class="docs-sidebar-close" @click="sidebarOpen = false" aria-label="Κλείσιμο">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="docs-sidebar-body">
          <template v-for="section in sections" :key="section.label">
            <div class="docs-section-label">{{ section.label }}</div>
            <nav class="docs-nav">
              <template v-for="item in section.items" :key="item.id">

                <!-- Expandable group -->
                <template v-if="item.children">
                  <button class="docs-nav-item docs-nav-parent"
                          :class="{ expanded: expandedGroups.includes(item.id), 'child-active': hasActiveChild(item) }"
                          :aria-expanded="expandedGroups.includes(item.id)"
                          @click="toggleGroup(item.id)">
                    <span class="docs-nav-icon" v-html="item.icon"></span>
                    <span class="docs-nav-label">{{ item.label }}</span>
                    <svg class="docs-nav-chevron" :class="{ rotated: expandedGroups.includes(item.id) }"
                         width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </button>
                  <div class="docs-nav-children" :class="{ open: expandedGroups.includes(item.id) }">
                    <button v-for="child in item.children" :key="child.id"
                            class="docs-nav-item docs-nav-child"
                            :class="{ active: activeId === child.id }"
                            @click="setActive(child.id)">
                      {{ child.label }}
                    </button>
                  </div>
                </template>

                <!-- Simple item -->
                <button v-else class="docs-nav-item"
                        :class="{ active: activeId === item.id }"
                        @click="setActive(item.id)">
                  <span class="docs-nav-icon" v-html="item.icon"></span>
                  <span class="docs-nav-label">{{ item.label }}</span>
                  <span v-if="item.starter" class="docs-starter-badge">STARTER</span>
                </button>

              </template>
            </nav>
          </template>
        </div>
      </aside>

      <!-- ══════════════════════════════════════════
           MAIN CONTENT
      ══════════════════════════════════════════ -->
      <main class="docs-content">
        <template v-if="activeItem">

          <!-- PDF viewer -->
          <template v-if="activeItem.pdf">
            <div class="docs-pdf-wrapper">
              <iframe
                :src="activeItem.pdf"
                class="docs-pdf-frame"
                :title="activeItem.label"
              ></iframe>
            </div>
          </template>

          <!-- Placeholder (no PDF yet) -->
          <template v-else>
            <div class="docs-placeholder">
              <div class="docs-placeholder-icon" v-html="activeItem.icon || defaultIcon"></div>
              <h2>{{ activeItem.label }}</h2>
              <p>Το αρχείο τεκμηρίωσης για αυτή την ενότητα θα είναι σύντομα διαθέσιμο.</p>
              <div class="docs-placeholder-badge">Σύντομα</div>
            </div>
          </template>

        </template>

        <!-- Welcome screen (nothing selected) -->
        <template v-else>
          <div class="docs-welcome">
            <img src="@/assets/CTGYM.YELLOW 1080x1080.svg" alt="Cloudtec Gym" class="docs-welcome-logo" />
            <h1>Τεκμηρίωση <span class="accent">Cloudtec Gym</span></h1>
            <p>Επίλεξε μια ενότητα από το μενού αριστερά για να δεις την αντίστοιχη τεκμηρίωση.</p>
          </div>
        </template>
      </main>

    </div>
  </div>
</template>

<script>
import GymNavBar from '@/components/Products/CloudtecGym/GymNavBar.vue';

const ICONS = {
  dashboard: `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>`,
  members:   `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>`,
  classes:   `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M11.5 2C6.81 2 3 5.81 3 10.5S6.81 19 11.5 19h.5v3c4.86-2.34 8-7 8-11.5C20 5.81 16.19 2 11.5 2zm1 14.5h-2v-2h2v2zm0-4h-2c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2h-2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.5-3 2.75-3 5z"/></svg>`,
  categories:`<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg>`,
  coaches:   `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/></svg>`,
  qrcheckin: `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM13 13h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm-2 4h2v2h-2zm2 0h2v2h-2z"/></svg>`,
  programming:`<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>`,
  bookings:  `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>`,
  workouts:  `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29z"/></svg>`,
  questionnaires:`<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6zm7-5h-4v-2h4v2zm2-4H9v-2h6v2z"/></svg>`,
  subscriptions:`<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg>`,
  finances:  `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>`,
  settings:  `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>`,
  support:   `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>`,
};

export default {
  name: 'GymDocs',
  components: { GymNavBar },

  data() {
    return {
      activeId: null,
      sidebarOpen: false,
      expandedGroups: ['programming', 'bookings', 'subscriptions'],

      sections: [
        {
          label: 'ΚΥΡΙΑ',
          items: [
            { id: 'dashboard',  label: 'Dashboard',      icon: ICONS.dashboard,  pdf: '/docs/Dashboard.pdf' },
            { id: 'members',    label: 'Μέλη',           icon: ICONS.members,    pdf: '/docs/ΜΕΛΗ.pdf' },
            { id: 'classes',    label: 'Τμήματα',        icon: ICONS.classes,    pdf: '/docs/TMHMATA.pdf' },
            { id: 'categories', label: 'Κατηγορίες',     icon: ICONS.categories, pdf: '/docs/ΚΑΤΗΓΟΡΙΕΣ.pdf' },
            { id: 'coaches',    label: 'Γυμναστές',      icon: ICONS.coaches,    pdf: '/docs/ΓΥΜΝΑΣΤΕΣ.pdf' },
            { id: 'qrcheckin',  label: 'QR Check-in',    icon: ICONS.qrcheckin,  pdf: '/docs/QR Check-in.pdf' },
          ],
        },
        {
          label: 'ΔΙΑΧΕΙΡΙΣΗ',
          items: [
            {
              id: 'programming', label: 'Προγραμματισμός', icon: ICONS.programming,
              children: [
                { id: 'sessions',  label: 'Συνεδρίες',  pdf: '/docs/ΣΥΝΕΔΡΙΕΣ.pdf' },
                { id: 'programs',  label: 'Προγράμματα', pdf: '/docs/ΠΡΟΓΡΑΜΜΑΤΑ.pdf' },
              ],
            },
            {
              id: 'bookings-group', label: 'Κρατήσεις', icon: ICONS.bookings,
              children: [
                { id: 'bookings-list',     label: 'Κρατήσεις',                   pdf: '/docs/ΚΡΑΤΗΣΕΙΣ.pdf' },
                { id: 'bookings-schedule', label: 'Προγραμματισμός Κρατήσεων',   pdf: '/docs/ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ ΚΡΑΤΗΣΕΩΝ.pdf' },
              ],
            },
            { id: 'workouts',        label: 'Προπονήσεις',      icon: ICONS.workouts,       pdf: '/docs/ΠΡΟΠΟΝΗΣΕΙΣ.pdf', },
            { id: 'questionnaires',  label: 'Ερωτηματολόγια',   icon: ICONS.questionnaires, pdf: '/docs/ΕΡΩΤΗΜΑΤΟΛΟΓΙΑ.pdf', },
            {
              id: 'subscriptions', label: 'Συνδρομές', icon: ICONS.subscriptions,
              children: [
                { id: 'membership-plans', label: 'Πλάνα Συνδρομών', pdf: '/docs/ΠΛΑΝΑ ΣΥΝΔΡΟΜΗΣ.pdf' },
                { id: 'memberships',      label: 'Συνδρομές',        pdf: '/docs/ΣΥΝΔΡΟΜΕΣ.pdf' },
              ],
            },
            { id: 'finances', label: 'Οικονομικά', icon: ICONS.finances, pdf: '/docs/ΟΙΚΟΝΟΜΙΚΑ.pdf', },
          ],
        },
        {
          label: 'ΡΥΘΜΙΣΕΙΣ',
          items: [
            { id: 'settings', label: 'Ρυθμίσεις', icon: ICONS.settings, pdf: null },
          ],
        },
      ],
    };
  },

  computed: {
    activeItem() {
      if (!this.activeId) return null;
      for (const section of this.sections) {
        for (const item of section.items) {
          if (item.id === this.activeId) return item;
          if (item.children) {
            const child = item.children.find(c => c.id === this.activeId);
            if (child) return child;
          }
        }
      }
      return null;
    },

    defaultIcon() {
      return ICONS.dashboard;
    },
  },

  methods: {
    setActive(id) {
      this.activeId = id;
      this.sidebarOpen = false;
    },

    toggleGroup(id) {
      const idx = this.expandedGroups.indexOf(id);
      if (idx === -1) this.expandedGroups.push(id);
      else this.expandedGroups.splice(idx, 1);
    },

    hasActiveChild(item) {
      if (!item.children) return false;
      return item.children.some(c => c.id === this.activeId);
    },
  },
};
</script>

<style scoped>
/* ── Base ── */
.gym-page {
  background: #253649;
  color: #fff;
  font-family: inherit;
  min-height: 100vh;
  overflow-x: hidden;
}

.accent { color: #FFC947; }

/* ── Layout ── */
.docs-layout {
  display: flex;
  height: calc(100vh - 60px);
  overflow: hidden;
}

/* ══════════════════════════════════════════
   SIDEBAR
══════════════════════════════════════════ */
.docs-sidebar {
  width: 248px;
  flex-shrink: 0;
  background: #1a2535;
  border-right: 1px solid rgba(255,255,255,0.07);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.docs-sidebar-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1.1rem 1.2rem 0.9rem;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  font-size: 0.88rem;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
  flex-shrink: 0;
}

.docs-sidebar-body {
  overflow-y: auto;
  flex: 1;
  padding: 0.5rem 0 1.5rem;
}

.docs-sidebar-body::-webkit-scrollbar { width: 4px; }
.docs-sidebar-body::-webkit-scrollbar-track { background: transparent; }
.docs-sidebar-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }

.docs-section-label {
  padding: 1rem 1.2rem 0.3rem;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
}

.docs-nav {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 0 0.5rem;
}

.docs-nav-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: rgba(255,255,255,0.62);
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  transition: background 0.15s, color 0.15s;
  position: relative;
}

.docs-nav-item:hover {
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.9);
}

.docs-nav-item.active {
  background: rgba(255, 201, 71, 0.12);
  color: #FFC947;
  font-weight: 600;
}

.docs-nav-item.child-active {
  color: rgba(255,255,255,0.85);
}

.docs-nav-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.7;
}

.docs-nav-item.active .docs-nav-icon {
  opacity: 1;
}

.docs-nav-label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.docs-nav-chevron {
  flex-shrink: 0;
  margin-left: auto;
  opacity: 0.45;
  transition: transform 0.2s;
}

.docs-nav-chevron.rotated { transform: rotate(180deg); }

.docs-starter-badge {
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #FFC947;
  background: rgba(255, 201, 71, 0.12);
  border: 1px solid rgba(255, 201, 71, 0.25);
  border-radius: 999px;
  padding: 0.15rem 0.45rem;
  flex-shrink: 0;
  margin-left: auto;
}

/* Expandable children */
.docs-nav-group {
  display: flex;
  flex-direction: column;
}

.docs-nav-children {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.25s ease;
}

.docs-nav-children.open {
  max-height: 300px;
}

.docs-nav-child {
  padding-left: 2.5rem;
  font-size: 0.845rem;
  color: rgba(255,255,255,0.55);
}

.docs-nav-child:hover { color: rgba(255,255,255,0.85); }
.docs-nav-child.active { color: #FFC947; background: rgba(255,201,71,0.1); }

/* ══════════════════════════════════════════
   MAIN CONTENT
══════════════════════════════════════════ */
.docs-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #1e2e40;
}

/* PDF frame */
.docs-pdf-wrapper {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.docs-pdf-frame {
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
}

/* Placeholder */
.docs-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
  color: rgba(255,255,255,0.75);
}

.docs-placeholder-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,201,71,0.1);
  border-radius: 16px;
  color: #FFC947;
}

.docs-placeholder-icon :deep(svg) {
  width: 28px;
  height: 28px;
}

.docs-placeholder h2 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.docs-placeholder p {
  font-size: 0.95rem;
  color: rgba(255,255,255,0.55);
  max-width: 380px;
  margin: 0;
  line-height: 1.6;
}

.docs-placeholder-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #FFC947;
  background: rgba(255,201,71,0.1);
  border: 1px solid rgba(255,201,71,0.25);
  border-radius: 999px;
  padding: 0.3rem 0.9rem;
}

/* Welcome screen */
.docs-welcome {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  padding: 3rem;
  text-align: center;
}

.docs-welcome-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.9;
}

.docs-welcome h1 {
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0;
  color: #fff;
}

.docs-welcome p {
  font-size: 1rem;
  color: rgba(255,255,255,0.55);
  max-width: 420px;
  margin: 0;
  line-height: 1.6;
}

/* ══════════════════════════════════════════
   MOBILE TOP BAR (hidden on desktop)
══════════════════════════════════════════ */
.docs-mobile-bar {
  display: none;
}

.docs-backdrop {
  display: none;
}

.docs-sidebar-close {
  display: none;
}

/* ══════════════════════════════════════════
   RESPONSIVE
══════════════════════════════════════════ */
@media (max-width: 768px) {
  /* Layout becomes single column, content fills screen */
  .docs-layout {
    flex-direction: column;
    height: calc(100vh - 60px);
    overflow: hidden;
    position: relative;
  }

  /* Mobile top bar */
  .docs-mobile-bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0 1rem;
    height: 48px;
    background: #1a2535;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    flex-shrink: 0;
    z-index: 10;
  }

  .docs-mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px;
    color: rgba(255,255,255,0.85);
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s;
  }

  .docs-mobile-menu-btn:hover { background: rgba(255,255,255,0.12); }

  .docs-mobile-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: rgba(255,255,255,0.85);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Backdrop overlay */
  .docs-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.55);
    z-index: 49;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s;
  }

  .docs-backdrop.open {
    opacity: 1;
    pointer-events: all;
  }

  /* Sidebar slides in from the left */
  .docs-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 280px;
    max-width: 85vw;
    z-index: 50;
    transform: translateX(-100%);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    border-right: 1px solid rgba(255,255,255,0.1);
  }

  .docs-sidebar.open {
    transform: translateX(0);
  }

  /* Close button visible on mobile */
  .docs-sidebar-close {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    width: 28px;
    height: 28px;
    background: rgba(255,255,255,0.07);
    border: none;
    border-radius: 6px;
    color: rgba(255,255,255,0.6);
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;
  }

  .docs-sidebar-close:hover { background: rgba(255,255,255,0.12); color: #fff; }

  /* Content fills remaining height */
  .docs-content {
    flex: 1;
    overflow: hidden;
    height: calc(100vh - 60px - 48px);
  }

  .docs-pdf-wrapper {
    height: 100%;
  }

  .docs-pdf-frame {
    height: 100%;
  }
}
</style>
